"use client";

import { useState, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { captureFocus, lockDocumentScroll, trapTabKey, unlockDocumentScroll } from '@/app/Components/overlayBehavior';

interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: string;
  actionUrl?: string;
}

interface OnboardingTourProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Scarlet Sync!',
    content: 'Let\'s take a quick tour of the new shell so you know where search, assistant tools, and account controls now live.',
    target: 'body',
    position: 'top'
  },
  {
    id: 'dock',
    title: 'App dock',
    content: 'Use the dock to jump between your main apps. Search opens one finder for apps, courses, and campus data.',
    target: 'nav[data-tour="app-dock"]',
    position: 'top'
  },
  {
    id: 'sidebar',
    title: 'Application sidebar',
    content: 'Toggle the sidebar to browse every app. Your semester, create actions, notifications, and account controls live there too.',
    target: '[data-tour="app-sidebar-toggle"]',
    position: 'bottom',
    action: 'Open profile',
    actionUrl: '/profile'
  },
  {
    id: 'final',
    title: 'You\'re All Set!',
    content: 'You now know where the main navigation lives. Start with scheduling, explore courses through Scarlet Search, or jump into the assistant from the dock.',
    target: 'body',
    position: 'top'
  }
];

export default function OnboardingTour({ isVisible, onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPortalReady, setIsPortalReady] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    setIsPortalReady(true);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (trapTabKey(e, tooltipRef.current)) {
        return;
      } else if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        previousStep();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep, onSkip]);

  useEffect(() => {
    if (!isVisible || !isPortalReady) return;
    lockDocumentScroll();
    const releaseFocus = captureFocus(tooltipRef.current);
    return () => {
      releaseFocus();
      unlockDocumentScroll();
    };
  }, [isPortalReady, isVisible]);

  useEffect(() => {
    if (!isVisible || !isPortalReady) return;

    // Remove previous highlight
    if (highlightedElement) {
      highlightedElement.classList.remove('tour-highlight');
      setHighlightedElement(null);
    }

    // Prefer our explicit data attributes if available for reliability
    const stepSelector = tourSteps[currentStep].target;
    let currentTarget: Element | null = null;
    if (stepSelector.startsWith('data:')) {
      const href = stepSelector.replace('data:', '');
      // Find all elements that match and then pick the one that's currently visible
      const elements = document.querySelectorAll(`[data-tour="sidebar-link"][data-tour-href="${href}"]`);
      currentTarget = Array.from(elements).find(el => (el as HTMLElement).offsetParent !== null) || null;
    } else {
      currentTarget = document.querySelector(stepSelector);
    }
    // If we're targeting a sidebar link, prefer highlighting the clickable container div
    let highlightTarget: Element | null = currentTarget;
    if (currentTarget && currentTarget.tagName === 'A') {
      const container = (currentTarget as HTMLElement).querySelector('div');
      if (container) highlightTarget = container;
    }
    if (currentTarget && tooltipRef.current) {
      const rect = currentTarget.getBoundingClientRect();
      const tooltip = tooltipRef.current;
      
      // Add highlight to current target (except for body)
      if (tourSteps[currentStep].target !== 'body' && highlightTarget) {
        highlightTarget.classList.add('tour-highlight');
        setHighlightedElement(highlightTarget);
      }
      
      // Position the tooltip
      const position = tourSteps[currentStep].position;
      let top = 0;
      let left = 0;

      // Handle special cases for body target
      if (tourSteps[currentStep].target === 'body') {
        top = window.innerHeight / 2 - tooltip.offsetHeight / 2;
        left = window.innerWidth / 2 - tooltip.offsetWidth / 2;
      } else {
        switch (position) {
          case 'top':
            top = rect.top - tooltip.offsetHeight - 20;
            left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
            break;
          case 'bottom':
            top = rect.bottom + 20;
            left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
            break;
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
            left = rect.left - tooltip.offsetWidth - 20;
            break;
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
            left = rect.right + 20;
            // Ensure tooltip doesn't go off-screen to the right
            if (left + tooltip.offsetWidth > window.innerWidth - 20) {
              left = rect.left - tooltip.offsetWidth - 20; // Position to the left instead
            }
            break;
        }
      }

      // Ensure tooltip stays within viewport
      top = Math.max(20, Math.min(top, window.innerHeight - tooltip.offsetHeight - 20));
      left = Math.max(20, Math.min(left, window.innerWidth - tooltip.offsetWidth - 20));

      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;

      // Scroll target into view if needed (but not for body target)
      if (tourSteps[currentStep].target !== 'body') {
        currentTarget.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    }
  }, [currentStep, isVisible, isPortalReady, highlightedElement]);

  // Cleanup highlights when tour is closed
  useEffect(() => {
    if (!isVisible && highlightedElement) {
      highlightedElement.classList.remove('tour-highlight');
      setHighlightedElement(null);
    }
  }, [isVisible, highlightedElement]);

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleActionClick = () => {
    const step = tourSteps[currentStep];
    if (step.actionUrl) {
      window.location.href = step.actionUrl;
    }
  };

  if (!isVisible || !isPortalReady) return null;

  const currentTourStep = tourSteps[currentStep];

  return createPortal(
    <>
      {/* Overlay */}
      <div 
        ref={overlayRef}
        className="ss-overlay-backdrop fixed inset-0 z-40"
        data-ss-overlay-kind="onboarding-tour"
        onClick={onSkip}
      />
      
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-ss-overlay-material="transient"
        data-ss-overlay-scroll-region
        className="ss-overlay-panel fixed z-50 w-[min(30rem,calc(100vw-2rem))] overflow-y-auto rounded-feature border p-4 shadow-e3 animate-fade-in motion-reduce:animate-none sm:p-6"
        style={{ 
          position: 'fixed',
          zIndex: 50
        }}
      >
        {/* Progress indicator */}
        {/* One escape hatch, not three. Skipping used to be a full-width bordered
            button above the heading, where it outranked the step it was letting you
            leave; Close and the footer's Esc hint already cover it. */}
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-[var(--ss-brand)] motion-safe:animate-pulse" />
            <span className="text-sm text-[var(--ss-text-muted)]">
              Step {currentStep + 1} of {tourSteps.length}
            </span>
          </div>
          <button
            onClick={onSkip}
            className="grid min-h-12 min-w-12 place-items-center rounded-control text-[var(--ss-text-muted)] transition-colors hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none"
            aria-label="Close tour"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <h3 id={titleId} className="mb-2 text-lg font-semibold text-[var(--ss-text)]">
          {currentTourStep.title}
        </h3>
        <p className="mb-4 text-[var(--ss-text-muted)]">
          {currentTourStep.content}
        </p>

        {/* Action button if available */}
        {currentTourStep.action && (
          <button
            onClick={handleActionClick}
            className="mb-4 min-h-12 w-full rounded-control bg-[var(--ss-brand)] px-4 py-2 font-medium text-[var(--ss-brand-contrast)] transition-colors hover:bg-[var(--ss-brand-strong)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
          >
            {currentTourStep.action}
          </button>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={previousStep}
            disabled={currentStep === 0}
            className="min-h-12 rounded-control border border-transparent px-4 py-2 text-[var(--ss-text-muted)] transition-colors hover:border-[var(--ss-border)] hover:text-[var(--ss-text)] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
          >
            Previous
          </button>
          
          <div className="hidden sm:flex gap-2">
            {tourSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                aria-label={`Go to tour step ${index + 1}`}
                aria-current={index === currentStep ? 'step' : undefined}
                className="grid min-h-12 min-w-12 place-items-center rounded-control transition-colors hover:bg-[var(--ss-surface-subtle)] motion-reduce:transition-none"
              >
                <span className={`h-2 w-2 rounded-full ${index === currentStep ? 'bg-[var(--ss-brand)]' : 'bg-slate-300 dark:bg-slate-600'}`} />
              </button>
            ))}
          </div>

          <button
            onClick={nextStep}
            className="min-h-12 rounded-control bg-[var(--ss-brand)] px-5 py-2.5 font-semibold text-[var(--ss-brand-contrast)] shadow-e1 transition-colors hover:bg-[var(--ss-brand-strong)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
          >
            {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="mt-4 text-xs text-slate-400 dark:text-slate-500 text-center">
          <span>Use ← → arrows or spacebar to navigate • Esc to skip</span>
        </div>
      </div>
    </>,
    document.body
  );
}
